import { Accommodation, Day, Transport, TripConfig } from '../types';
import { destTitle, transportDuringVisit } from './destinationItems';
import { sameTransportLeg } from './journeys';
import { boundaryTransport } from './scheduleTravel';
import { destinationKey } from './destinationItems';
import { addDays } from './travelState';
import { assignedTravellers } from '../../functions/src/travellerRoutePolicy.mjs';
import { bookingConflicts as detectBookingConflicts, sharesTraveller, uniqueConflictStays, conflictAudience } from '../../functions/src/bookingConflicts.mjs';

export { sharesTraveller } from "../../functions/src/bookingConflicts.mjs";
type Assignable = { participantIds?: string[]; contributedBy?: string; addedBy?: string };

// Moved out of ItineraryGapsModal.tsx: this is pure detection logic with no
// React in it, and living in a .tsx file meant it could only be tested by
// pulling in react/motion/lucide. The modal still owns the rendering.

const formatCount = (n: number, capitalizeFirst: boolean = false): string => {
  if (n >= 10) return n.toString();
  const words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
  const w = words[n] || n.toString();
  return capitalizeFirst ? w.charAt(0).toUpperCase() + w.slice(1) : w;
};

/** Format a YYYY-MM-DD (or ISO) string as "Sat 10 Feb". Uses noon to avoid UTC-offset issues. */
const fmtD = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr.length === 10 ? dateStr + 'T12:00' : dateStr);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

/** Format an inclusive date range as "Sat 10 Feb - Mon 12 Feb" (or just "Sat 10 Feb" when single day). */
const fmtRange = (startDate: string, endDate: string) => {
  const s = fmtD(startDate);
  const e = fmtD(endDate);
  return s === e || !e ? s : `${s} - ${e}`;
};

/** A date field carries a usable time only in the `YYYY-MM-DDTHH:MM` form —
 *  a date-only value is what an import or a partial edit leaves behind, and
 *  it's the thing the Schedule, durations and countdowns can't work with.
 *  Same test `scheduleTravel.ts` uses to decide a booking is schedulable. */
const hasTime = (v: string | undefined) => /T\d{2}:\d{2}/.test(v || '');

/** "No departure time" / "No departure or arrival time" — name only what's
 *  actually missing, so the row says what to go and fix. */
const missingTimeLabel = (startMissing: boolean, endMissing: boolean, startWord: string, endWord: string) =>
  startMissing && endMissing ? `No ${startWord} or ${endWord} time`
    : startMissing ? `No ${startWord} time`
      : `No ${endWord} time`;

export interface InsightItemData {
  id: string;
  label: string;
  sublabel?: string;
  onClick: () => void;
}

export interface Insight {
  id: string;
  type: 'DESTINATION' | 'ACCOMMODATION' | 'TRANSPORT';
  /** 'essential': missing outright; 'details': missing times (both Gaps);
   *  'review': saved records that may conflict, never automatically removed (Conflicts). */
  group: 'essential' | 'details' | 'review';
  message: string;
  items: InsightItemData[];
}

type SavedLeg = Transport & { _dayDate?: string };

/** The same traveller in two places at once. Stays compare local nights
 *  (checkout day = next check-in is fine); timed legs compare real instants,
 *  so only legs with both zones known are checked; exact duplicates are a
 *  separate review. Shared by Itinerary review and conflict alerts. */
export const bookingConflicts = detectBookingConflicts<Accommodation, SavedLeg>;

/**
 * Same detection logic as before (missing destinations, unbooked
 * accommodation nights, missing transport legs, destinations with zero
 * activities) — unchanged, just parameterized on two navigation callbacks
 * instead of Home's own cross-tab `navigateAndDispatch`, since this is now
 * called from *within* the Itinerary tab:
 *  - onDestinationsGap(gapStart, gapEnd): open the single-destination "Add
 *    destination" panel pre-filled with that gap's dates — the same panel
 *    the "Add destinations" block on the Itinerary tab itself opens, not
 *    the full multi-destination list (no tab switch needed either way,
 *    TripOverview already hosts both).
 *  - onScrollToDestination(destIdx): missing accommodation scrolls to its
 *    destination card. An existing stay missing times opens its item modal.
 *  - onScrollToTransport(toDestIdx): Transport is different — a leg lives
 *    *between* two destination cards, not on either one, so it gets its own
 *    callback landing on the connecting transport row itself rather than a
 *    destination card. Existing legs missing times open their item modal.
 *
 * "Activities" (destinations with zero attractions/eats/nightlife planned)
 * is deliberately NOT detected here any more — dropped per explicit
 * request, was the 4th insight type alongside these three.
 *
 * Each of Accommodation and Transport reports two kinds of gap in one
 * accordion: something missing entirely, and something booked but missing
 * its times. The second is the more invisible of the two — the entry looks
 * complete on the card — and it's what stops a booking getting a Schedule
 * slot, a duration or a countdown.
 */
export function computeItineraryGaps(
  itineraryDays: Day[],
  tripConfig: TripConfig,
  callbacks: {
    onDestinationsGap: (gapStart: string, gapEnd: string) => void;
    onScrollToDestination: (destIdx: number) => void;
    /** Transport legs live *between* two destination cards, not on either
     *  one — so unlike Accommodation (which is genuinely a property of one
     *  destination), a transport gap gets its own callback rather than
     *  reusing onScrollToDestination, landing on the connecting transport
     *  row itself. `toDestIdx` is the arrival destination's index (same
     *  index space destIdxForCity already produces) — TripOverview resolves
     *  that back to the specific transport row's own DOM id. */
    onScrollToTransport: (toDestIdx: number) => void;
    onOpenAccommodation: (accommodation: Accommodation, dayDate: string) => void;
    onOpenTransport: (transport: Transport, dayDate: string) => void;
  },
  /** Whose coverage counts: Just me passes [you]; default is the scope's members. */
  travellerIds?: string[],
  /** People away on a private branch that day (All view, from safe junction events). */
  away?: (date: string) => string[],
  /** All-view warnings still concern only the affected people/booking managers. */
  viewerId?: string,
): Insight[] {
  const { onDestinationsGap, onScrollToDestination, onScrollToTransport, onOpenAccommodation, onOpenTransport } = callbacks;
  const insights: Insight[] = [];
  const eligible = travellerIds ?? tripConfig.memberIds ?? (tripConfig.travellers || []).map(t => t.id);
  const visibleConflict = (a: Accommodation | Transport, b: Accommodation | Transport) => !viewerId
    || conflictAudience(a, b, eligible, [tripConfig.ownerId, ...(tripConfig.adminIds ?? [])]).includes(viewerId);
  const firstName = (id: string) => (tripConfig.travellers || []).find(t => t.id === id)?.name.split(' ')[0] || 'A traveller';
  // Travellers left uncovered by these bookings; [] = everyone covered.
  // With no bookings at all, everyone is uncovered (even with no roster).
  // `who`: the people who count there (eligible, minus anyone away on a branch).
  const present = (date: string, overnight = false) => eligible.filter(id => {
    const dates = tripConfig.participation?.[id];
    return !away?.(date).includes(id) && (!dates || date >= dates.startDate && (overnight ? date < dates.endDate : date <= dates.endDate));
  });
  // Everyone away (a known roster, nobody here) is no gap; an unknown roster still is.
  const uncovered = (bookings: Assignable[], who: string[]) => eligible.length && !who.length ? [] : !bookings.length ? ['*']
    : who.filter(id => !bookings.some(b => assignedTravellers(b.participantIds, eligible).includes(id)));
  // "Rome" when everyone there is missing it, "Rome · Bob" when only some are.
  const forWhom = (label: string, missing: string[], who: string[]) =>
    missing[0] === '*' || missing.length === who.length ? label : `${label} · ${missing.map(firstName).join(', ')}`;

  // Resolve a "City" or "City, Country" label back to its index in
  // tripConfig.destinations — the index space TripOverview's own
  // cityGroups[].destIdx (and therefore its `itinerary-dest-{idx}` card
  // ids) already uses.
  const destIdxForCity = (city: string): number =>
    (tripConfig.destinations || []).findIndex(dest => destTitle(dest.name) === city);
  // Name-only resolution breaks whenever a trip visits the same city twice
  // (a round-trip or hub itinerary) — findIndex always returns the FIRST
  // match, so every click-through for a later visit silently resolves to
  // the earlier one instead. Where the actual Destination object is
  // already in hand (sortedDests, below), resolve by that object's own
  // identity instead — sortedDests is built from tripConfig.destinations
  // via spread+filter+sort, which reorders/selects but never clones, so
  // its elements are the exact same references indexOf can find.
  const destIdxForDest = (dest: { name: string; arrivalDate: string }): number =>
    (tripConfig.destinations || []).indexOf(dest as (typeof tripConfig.destinations)[number]);

  // DESTINATION
  const destItems: InsightItemData[] = [];
  const missingDestDays = itineraryDays.map((d, i) => ({ d, i })).filter(({ d }) => (!eligible.length || present(d.date).length) && (!d.city || d.city === "Gap in Itinerary"));

  let currentDestGroup: { start: Day, end: Day, indices: number[] } | null = null;
  missingDestDays.forEach(({ d, i }, idx) => {
    if (!currentDestGroup) {
      currentDestGroup = { start: d, end: d, indices: [i] };
    } else {
      const lastMissing = missingDestDays[idx - 1];
      if (lastMissing.i === i - 1) {
        currentDestGroup.end = d;
        currentDestGroup.indices.push(i);
      } else {
        // Capture this group's dates in locals before onClick closes over
        // them — currentDestGroup itself gets reassigned to the next group
        // right below, so a closure reading currentDestGroup directly would
        // fire with the wrong (last-seen) gap's dates.
        const gapStart = currentDestGroup.start.date;
        const gapEnd = currentDestGroup.end.date;
        destItems.push({
          id: `dest-${gapStart}`,
          label: fmtRange(gapStart, gapEnd),
          onClick: () => onDestinationsGap(gapStart, gapEnd),
        });
        currentDestGroup = { start: d, end: d, indices: [i] };
      }
    }
  });
  if (currentDestGroup) {
    const gapStart = currentDestGroup.start.date;
    const gapEnd = currentDestGroup.end.date;
    destItems.push({
      id: `dest-${gapStart}`,
      label: fmtRange(gapStart, gapEnd),
      onClick: () => onDestinationsGap(gapStart, gapEnd),
    });
  }

  if (destItems.length > 0) {
    insights.push({
      id: 'missing-dest',
      type: 'DESTINATION',
      group: 'essential',
      message: `${formatCount(missingDestDays.length, true)} ${missingDestDays.length === 1 ? 'day is' : 'days are'} missing a destination.`,
      items: destItems
    });
  }

  // ACCOMMODATION
  const accItems: InsightItemData[] = [];
  const missingAccDays = itineraryDays.slice(0, -1).map((d, i) => ({ d, i,
    who: uncovered([d.accommodation, ...(d.accommodations || [])].filter(a => a?.name), present(d.date, true)) }))
    .filter(({ d, who }) => d.city && d.city !== "Gap in Itinerary" && who.length > 0);

  let currentAccGroup: { city: string, label: string, start: Day, end: Day, targetIdx: number } | null = null;
  missingAccDays.forEach(({ d, i, who }, idx) => {
    if (!currentAccGroup) {
      currentAccGroup = { city: d.city, label: forWhom(d.city, who, present(d.date)), start: d, end: d, targetIdx: i };
    } else {
      const lastMissing = missingAccDays[idx - 1];
      if (lastMissing.i === i - 1 && lastMissing.d.city === d.city && currentAccGroup.label === forWhom(d.city, who, present(d.date))) {
        currentAccGroup.end = d;
      } else {
        const accEndDest = tripConfig.destinations?.find(dest =>
          destTitle(dest.name) === currentAccGroup!.city && dest.departureDate
        );
        const accEndDate = Object.keys(tripConfig.participation ?? {}).length ? addDays(currentAccGroup.end.date, 1) : accEndDest?.departureDate || currentAccGroup.end.date;
        accItems.push({
          id: `acc-${currentAccGroup.start.date}`,
          label: currentAccGroup.label,
          sublabel: fmtRange(currentAccGroup.start.date, accEndDate),
          onClick: ((city: string) => () => onScrollToDestination(destIdxForCity(city)))(currentAccGroup.city)
        });
        currentAccGroup = { city: d.city, label: forWhom(d.city, who, present(d.date)), start: d, end: d, targetIdx: i };
      }
    }
  });
  if (currentAccGroup) {
    const accEndDest = tripConfig.destinations?.find(dest =>
      destTitle(dest.name) === currentAccGroup!.city && dest.departureDate
    );
    const accEndDate = Object.keys(tripConfig.participation ?? {}).length ? addDays(currentAccGroup.end.date, 1) : accEndDest?.departureDate || currentAccGroup.end.date;
    accItems.push({
      id: `acc-${currentAccGroup.start.date}`,
      label: currentAccGroup.label,
      sublabel: fmtRange(currentAccGroup.start.date, accEndDate),
      onClick: ((city: string) => () => onScrollToDestination(destIdxForCity(city)))(currentAccGroup.city)
    });
  }

  // ACCOMMODATION — booked, but with no usable check-in/check-out time.
  // A stay is copied onto every Day it covers, so the same booking shows up
  // once per night: key it by identity (id, else name+dates) and report it
  // once. Every day is scanned, not slice(0, -1) — the last day's copy is
  // the same booking, and skipping it would only matter for a one-day stay
  // ending on the final day, which is still a real gap.
  const seenAcc = new Set<string>();
  const accTimeItems: InsightItemData[] = [];
  itineraryDays.forEach(d => {
    const stays: Accommodation[] = [d.accommodation, ...(d.accommodations || [])].filter(a => a?.name);
    stays.forEach(a => {
      const key = a.id || `${a.name}|${a.checkIn}|${a.checkOut}`;
      if (seenAcc.has(key)) return;
      seenAcc.add(key);
      const noIn = !hasTime(a.checkIn);
      const noOut = !hasTime(a.checkOut);
      if (!noIn && !noOut) return;
      accTimeItems.push({
        id: `acc-incomplete-${key}`,
        label: a.name,
        sublabel: missingTimeLabel(noIn, noOut, 'check-in', 'check-out'),
        onClick: () => onOpenAccommodation(a, d.date),
      });
    });
  });

  if (accItems.length > 0) {
    insights.push({
      id: 'missing-acc',
      type: 'ACCOMMODATION',
      group: 'essential',
      message: `No accommodation booked for ${formatCount(missingAccDays.length)} ${missingAccDays.length === 1 ? 'night' : 'nights'}.`,
      items: accItems
    });
  }
  if (accTimeItems.length > 0) {
    const n = accTimeItems.length;
    insights.push({
      id: 'acc-times',
      type: 'ACCOMMODATION',
      group: 'details',
      message: `${formatCount(n, true)} ${n === 1 ? 'stay is missing a check-in or check-out time' : 'stays are missing check-in or check-out times'}.`,
      items: accTimeItems
    });
  }

  // TRANSPORT — destination-level check.
  const transportItems: InsightItemData[] = [];
  const allTransports = itineraryDays.flatMap(d => (d.transports || []).map(t => ({ ...t, _dayDate: d.date })));
  // Inspect raw saved records too: an edited date can sit outside the expanded trip days.
  const savedTransports = tripConfig.itinerary && Object.keys(tripConfig.itinerary).length
    ? Object.entries(tripConfig.itinerary).flatMap(([date, d]) => (d.transports || []).map(t => ({ ...t, _dayDate: date })))
    : allTransports;
  const duplicateItems = savedTransports.filter((t, i, all) =>
    (!t.id || all.findIndex(other => other.id === t.id) === i) && all.some(other => sameTransportLeg(t, other) && sharesTraveller(t, other, eligible) && visibleConflict(t, other)));
  if (duplicateItems.length) insights.push({
    id: 'transport-duplicates', type: 'TRANSPORT', group: 'review',
    message: 'Possible duplicate transport.',
    items: duplicateItems.map((t, i) => ({
      id: `transport-duplicate-${t.id || i}`,
      label: `${t.fromCity || t.from} → ${t.toCity || t.to}`,
      sublabel: `${fmtD(t.dep)} · ${t.dep.split('T')[1] || ''} · ${t.carrier || t.mode}${t.flightNum ? ` ${t.flightNum}` : ''}`,
      onClick: () => onOpenTransport(t, t._dayDate),
    })),
  });
  const legacyStays = Object.entries(tripConfig.itinerary || {}).flatMap(([date, d]) => d.accommodation ? [{ stay: d.accommodation, date }] : []);
  const stays = uniqueConflictStays([...(tripConfig.accommodations || []),
    ...legacyStays.map(row => row.stay),
  ].filter(a => a.checkIn && a.checkOut));
  // A changed attendance window never edits an existing reservation or its expense.
  // Explicit assignments wholly outside that window stay reachable for review.
  const outside = (item: Assignable, start: string, end: string) => !!start && (item.participantIds ?? []).some(id => {
    const dates = tripConfig.participation?.[id];
    return eligible.includes(id) && dates && (end.slice(0, 10) < dates.startDate || start.slice(0, 10) > dates.endDate)
      && (!viewerId || viewerId === id || viewerId === item.contributedBy || viewerId === item.addedBy
        || viewerId === tripConfig.ownerId || tripConfig.adminIds?.includes(viewerId));
  });
  const outsideItems: InsightItemData[] = [
    ...stays.filter(a => outside(a, a.checkIn, a.checkOut)).map(a => ({ id: `dates-${a.id}`, label: a.name,
      sublabel: fmtRange(a.checkIn, a.checkOut), onClick: () => onOpenAccommodation(a, legacyStays.find(row => row.stay === a)?.date ?? a.checkIn.slice(0, 10)) })),
    ...savedTransports.filter(t => outside(t, t.dep, t.arr || t.dep)).map(t => ({ id: `dates-${t.id}`, label: `${t.fromCity || t.from} → ${t.toCity || t.to}`,
      sublabel: fmtRange(t.dep, t.arr), onClick: () => onOpenTransport(t, t._dayDate) })),
  ];
  const seenDatedItems = new Set<string>();
  for (const [date, day] of Object.entries(tripConfig.itinerary ?? {})) {
    for (const item of [...(day.attractions ?? []), ...(day.eats ?? []), ...(day.nightlife ?? []), ...(day.shopping ?? [])]) {
      if (!item.bookingDate || seenDatedItems.has(item.id) || !outside(item, item.bookingDate, item.bookingDate)) continue;
      seenDatedItems.add(item.id);
      const index = tripConfig.destinations.findIndex(d => date >= d.arrivalDate && date <= d.departureDate);
      outsideItems.push({ id: `dates-${item.id}`, label: item.name, sublabel: fmtD(item.bookingDate), onClick: () => onScrollToDestination(index) });
    }
  }
  if (outsideItems.length) insights.push({ id: 'participation-booking-dates', type: 'DESTINATION', group: 'review',
    message: 'Bookings outside a traveller’s trip dates.', items: outsideItems });
  const conflicts = bookingConflicts(stays, savedTransports, eligible).filter(c => visibleConflict(c.a, c.b));
  const inConflict = (item: object) => conflicts.some(c => c.a === item || c.b === item);
  const night = (v: string) => v.slice(0, 10);
  const stayClashes = stays.filter(inConflict);
  if (stayClashes.length) insights.push({
    id: 'stay-overlaps', type: 'ACCOMMODATION', group: 'review',
    message: 'Overlapping stays for the same traveller.',
    items: stayClashes.map((a, i) => ({ id: `stay-overlap-${a.id || i}`, label: a.name, sublabel: fmtRange(a.checkIn, a.checkOut),
      onClick: () => onOpenAccommodation(a, legacyStays.find(row => row.stay === a)?.date ?? night(a.checkIn)) })),
  });
  const legClashes = savedTransports.filter(inConflict);
  if (legClashes.length) insights.push({
    id: 'transport-overlaps', type: 'TRANSPORT', group: 'review',
    message: 'Overlapping journeys for the same traveller. Review the times.',
    items: legClashes.map((t, i) => ({ id: `transport-overlap-${t.id || i}`, label: `${t.fromCity || t.from} → ${t.toCity || t.to}`,
      sublabel: `${fmtD(t.dep)} · ${t.dep.split('T')[1] || ''} · ${t.carrier || t.mode}`, onClick: () => onOpenTransport(t, t._dayDate) })),
  });

  const isJourney = (t: Transport) => !t.isRental && !(tripConfig.destinations || []).some(dest => transportDuringVisit(t, dest, allTransports));
  // A route is covered once everyone counted has a journey leg in its window.
  const routeMissing = (days: Day[], who: string[]) => uncovered(days.flatMap(d => (d.transports || []).filter(isJourney)), who);
  const sortedDests = [...(tripConfig.destinations || [])]
    .filter(d => d.name && d.arrivalDate)
    .sort((a, b) => (a.arrivalDate || '').localeCompare(b.arrivalDate || ''));

  for (const id of eligible) {
    const dates = tripConfig.participation?.[id];
    if (!dates) continue;
    for (const direction of ['arrival', 'departure'] as const) {
      const arriving = direction === 'arrival';
      const date = arriving ? dates.startDate : dates.endDate;
      const dest = sortedDests.find(d => destinationKey(d) === (arriving ? dates.startVisitId : dates.endVisitId));
      if (!dest || away?.(date).includes(id) || arriving && date === sortedDests[0]?.arrivalDate) continue;
      const personal = { ...dest, arrivalDate: arriving ? date : dest.arrivalDate, departureDate: arriving ? dest.departureDate : date };
      const legs = savedTransports.filter(t => assignedTravellers(t.participantIds, eligible).includes(id));
      if (boundaryTransport(personal, legs, direction)) continue;
      transportItems.push({ id: `transport-${direction}-${id}`, label: `${arriving ? 'To' : 'From'} ${destTitle(dest.name)} · ${firstName(id)}`,
        sublabel: fmtD(date), onClick: () => onScrollToDestination(destIdxForDest(dest)) });
    }
  }

  if (sortedDests.length > 0) {
    const firstDest = sortedDests[0];
    const firstDestDays = itineraryDays.filter(d =>
      d.city === destTitle(firstDest.name) && d.city !== 'Gap in Itinerary'
    );
    const firstWho = present(firstDest.arrivalDate);
    const firstMissing = routeMissing(firstDestDays, firstWho);
    if (firstMissing.length) {
      transportItems.push({
        id: 'transport-first',
        label: forWhom(`To ${destTitle(firstDest.name)}`, firstMissing, firstWho),
        sublabel: fmtD(firstDest.arrivalDate),
        onClick: () => onScrollToTransport(destIdxForDest(firstDest)),
      });
    }

    for (let i = 1; i < sortedDests.length; i++) {
      const fromDest = sortedDests[i - 1];
      const toDest = sortedDests[i];
      const fromCity = destTitle(fromDest.name);
      const toCity = destTitle(toDest.name);
      const depDate = fromDest.departureDate || fromDest.arrivalDate || '';
      const arrDate = toDest.arrivalDate || '';

      const windowDays = itineraryDays.filter(d => d.date >= depDate && d.date <= arrDate);
      // Only people at both ends travel this leg on the shared route.
      const who = present(depDate).filter(id => {
        const dates = tripConfig.participation?.[id];
        return present(arrDate).includes(id) && (!dates ||
          sortedDests.findIndex(d => destinationKey(d) === dates.startVisitId) <= i - 1 &&
          sortedDests.findIndex(d => destinationKey(d) === dates.endVisitId) >= i);
      });
      const missing = routeMissing(windowDays, who);

      if (missing.length) {
        transportItems.push({
          id: `transport-dest-${i}`,
          label: forWhom(`${fromCity} → ${toCity}`, missing, who),
          sublabel: fmtRange(depDate, arrDate),
          onClick: () => onScrollToTransport(destIdxForDest(toDest)),
        });
      }
    }
  }

  // TRANSPORT — booked, but with no usable departure/arrival time. Same
  // dedupe reasoning as accommodation: a leg is filed under its departure
  // date, but readers match either end of the journey, so key it by identity
  // and report it once.
  const seenTransport = new Set<string>();
  const transportTimeItems: InsightItemData[] = [];
  itineraryDays.forEach(d => {
    (d.transports || []).forEach((t: Transport) => {
      const key = t.id || `${t.mode}|${t.flightNum}|${t.from}|${t.to}|${t.dep}|${t.arr}`;
      if (seenTransport.has(key)) return;
      seenTransport.add(key);
      const noDep = !hasTime(t.dep);
      const noArr = !hasTime(t.arr);
      if (!noDep && !noArr) return;
      const toCity = (t.toCity || t.to || '').split(',')[0].trim();
      transportTimeItems.push({
        id: `transport-incomplete-${key}`,
        label: `${(t.fromCity || t.from || '').split(',')[0].trim()} → ${toCity}`,
        sublabel: missingTimeLabel(noDep, noArr, t.isRental ? 'pickup' : 'departure', t.isRental ? 'return' : 'arrival'),
        onClick: () => onOpenTransport(t, d.date),
      });
    });
  });

  if (transportItems.length > 0) {
    const n = transportItems.length;
    insights.push({
      id: 'missing-transport',
      type: 'TRANSPORT',
      group: 'essential',
      message: `Missing transport for ${formatCount(n)} ${n === 1 ? 'route' : 'routes'}.`,
      items: transportItems
    });
  }
  if (transportTimeItems.length > 0) {
    const n = transportTimeItems.length;
    insights.push({
      id: 'transport-times',
      type: 'TRANSPORT',
      group: 'details',
      message: `${formatCount(n, true)} ${n === 1 ? 'leg is missing a departure or arrival time' : 'legs are missing departure or arrival times'}.`,
      items: transportTimeItems
    });
  }

  return insights;
}

export type ScheduleGap = 'none' | 'some' | null;

/**
 * The Schedule's "what's missing" banner headline for one destination, in
 * every combination: nothing at all ("No transport or accommodation yet"),
 * partly there ("Some transport missing" — an arrival booked but no onward
 * leg, or a stay that doesn't cover every night), or a mix of the two.
 */
export function scheduleGapHeadline(gaps: { transport: ScheduleGap; accommodation: ScheduleGap; shortlist: boolean }): string | null {
  const list = (parts: string[]) => parts.length <= 1 ? parts[0] ?? '' : `${parts.slice(0, -1).join(', ')} or ${parts[parts.length - 1]}`;
  const andList = (parts: string[]) => list(parts).replace(/ or (?=[^,]*$)/, ' and ');
  const none = [gaps.transport === 'none' && 'transport', gaps.accommodation === 'none' && 'accommodation', gaps.shortlist && 'shortlist items saved'].filter(Boolean) as string[];
  const some = [gaps.transport === 'some' && 'transport', gaps.accommodation === 'some' && 'accommodation'].filter(Boolean) as string[];
  const noneText = none.length ? `No ${list(none)} yet` : '';
  const someText = some.length ? `${none.length ? 'some' : 'Some'} ${andList(some)} missing` : '';
  return noneText && someText ? `${noneText}, and ${someText}` : noneText || someText || null;
}
